/**
 * Filter Utilities for DB Interface
 * Types, migration, and URL encoding/decoding for filters
 */

// Filter operators supported by the system
export const FILTER_OPERATORS = {
  equals: { value: 'equals', label: 'Equals' },
  notEquals: { value: 'notEquals', label: 'Not Equals' },
  contains: { value: 'contains', label: 'Contains' },
  notContains: { value: 'notContains', label: 'Does Not Contain' },
  in: { value: 'in', label: 'In List' },
  notIn: { value: 'notIn', label: 'Not In List' },
  greaterThan: { value: 'greaterThan', label: 'Greater Than' },
  lessThan: { value: 'lessThan', label: 'Less Than' },
  greaterThanOrEqual: { value: 'greaterThanOrEqual', label: 'Greater Than or Equal' },
  lessThanOrEqual: { value: 'lessThanOrEqual', label: 'Less Than or Equal' },
  between: { value: 'between', label: 'Between' },
  isEmpty: { value: 'isEmpty', label: 'Is Empty' },
  isNotEmpty: { value: 'isNotEmpty', label: 'Is Not Empty' },
  isNull: { value: 'isNull', label: 'Is Null' },
  isNotNull: { value: 'isNotNull', label: 'Is Not Null' },
};

// Map legacy operator values to new operators
const LEGACY_OPERATOR_MAP = {
  'ILIKE': 'contains',
  'notILIKE': 'notContains',
  '=': 'equals',
  'IN': 'in',
  '!=': 'notEquals',
  '<>': 'notEquals',
  '>': 'greaterThan',
  '<': 'lessThan',
  '>=': 'greaterThanOrEqual',
  '<=': 'lessThanOrEqual',
};

// Operators available for different field types
export const getOperatorsForType = (type, isReference = false) => {
  if (isReference) {
    return [FILTER_OPERATORS.in];
  }

  switch (type) {
    case 'uuid':
      return [FILTER_OPERATORS.equals, FILTER_OPERATORS.in];
    
    case 'String':
      return [
        FILTER_OPERATORS.contains,
        FILTER_OPERATORS.notContains,
        FILTER_OPERATORS.equals,
        FILTER_OPERATORS.notEquals,
        FILTER_OPERATORS.isEmpty,
        FILTER_OPERATORS.isNotEmpty,
        FILTER_OPERATORS.isNull,
        FILTER_OPERATORS.isNotNull,
      ];
    
    case 'Boolean':
      return [FILTER_OPERATORS.equals];
    
    case 'Number':
    case 'real':
      return [
        FILTER_OPERATORS.equals,
        FILTER_OPERATORS.notEquals,
        FILTER_OPERATORS.greaterThan,
        FILTER_OPERATORS.lessThan,
        FILTER_OPERATORS.greaterThanOrEqual,
        FILTER_OPERATORS.lessThanOrEqual,
        FILTER_OPERATORS.between,
        FILTER_OPERATORS.isNull,
        FILTER_OPERATORS.isNotNull,
      ];
    
    case 'Date':
    case 'timestamp':
      return [
        FILTER_OPERATORS.equals,
        FILTER_OPERATORS.notEquals,
        FILTER_OPERATORS.greaterThan,
        FILTER_OPERATORS.lessThan,
        FILTER_OPERATORS.greaterThanOrEqual,
        FILTER_OPERATORS.lessThanOrEqual,
        FILTER_OPERATORS.between,
        FILTER_OPERATORS.isNull,
        FILTER_OPERATORS.isNotNull,
      ];
    
    default:
      return [
        FILTER_OPERATORS.contains,
        FILTER_OPERATORS.equals,
        FILTER_OPERATORS.isEmpty,
        FILTER_OPERATORS.isNotEmpty,
      ];
  }
};

/**
 * Check if an item is a filter group (has nested conditions)
 */
export const isFilterGroup = (item) => {
  return item && 'conditions' in item && Array.isArray(item.conditions);
};

/**
 * Check if an item is a filter condition (has field property)
 */
export const isFilterCondition = (item) => {
  return item && 'field' in item;
};

/**
 * Check if filters are in legacy format
 */
export const isLegacyFormat = (filters) => {
  return Array.isArray(filters) && 
    (filters.length === 0 || (filters[0] && 'column' in filters[0]));
};

/**
 * Convert legacy filter format to new format
 */
export const migrateFromLegacyFormat = (legacyFilters) => {
  if (!legacyFilters || legacyFilters.length === 0) {
    return null;
  }

  // List of valid new format operators
  const NEW_FORMAT_OPERATORS = ['equals', 'notEquals', 'contains', 'notContains', 'in', 'notIn',
    'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between',
    'isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'];

  const conditions = legacyFilters.map(item => {
    const legacyOperator = item.condition?.value || '=';
    
    // Check if operator is already in new format
    const isNewFormatOperator = NEW_FORMAT_OPERATORS.includes(legacyOperator);
    const newOperator = isNewFormatOperator 
      ? legacyOperator  // Already in new format, use as-is
      : (LEGACY_OPERATOR_MAP[legacyOperator] || 'equals');  // Map from legacy format
    
    let value;
    if (Array.isArray(item.value)) {
      // Multi-reference values
      value = item.value.map(v => v._id || v);
    } else if (item.value && typeof item.value === 'object' && item.value._id) {
      // Single reference value
      value = item.value._id;
    } else if ((legacyOperator === 'ILIKE' || (!isNewFormatOperator && legacyOperator === 'contains')) && typeof item.value === 'string') {
      // Remove % wildcards from ILIKE values (or legacy contains with %)
      value = item.value.replace(/^%|%$/g, '');
    } else {
      value = item.value;
    }
    
    return {
      field: item.column,
      operator: newOperator,
      value,
    };
  });
  
  return {
    version: 1,
    operator: 'AND',
    conditions,
  };
};

/**
 * Normalize filters to the new format
 */
export const normalizeFilters = (filters) => {
  if (!filters) {
    return null;
  }
  
  // Already in new format
  if (filters.operator && filters.conditions) {
    return filters;
  }
  
  // Legacy format - migrate
  if (isLegacyFormat(filters)) {
    if (filters.length === 0) {
      return null;
    }
    return migrateFromLegacyFormat(filters);
  }
  
  return null;
};

/**
 * Convert new format back to legacy format for backend compatibility
 * (Used during transition period)
 */
export const convertToLegacyFormat = (filterGroup) => {
  if (!filterGroup || !filterGroup.conditions) {
    return [];
  }

  // Only handle flat AND groups for backwards compatibility
  if (filterGroup.operator !== 'AND') {
    // For complex nested filters, send in new format
    return filterGroup;
  }

  // Check if all conditions are simple (no nested groups)
  const hasNestedGroups = filterGroup.conditions.some(isFilterGroup);
  if (hasNestedGroups) {
    // Send in new format for nested groups
    return filterGroup;
  }

  // Convert to legacy format
  return filterGroup.conditions.map(condition => {
    let legacyOperator = '=';
    let value = condition.value;

    switch (condition.operator) {
      case 'contains':
        legacyOperator = 'ILIKE';
        value = `%${value}%`;
        break;
      case 'notContains':
        legacyOperator = 'notILIKE';
        value = `%${value}%`;
        break;
      case 'in':
        legacyOperator = 'IN';
        // Convert array of IDs back to objects with _id
        if (Array.isArray(value)) {
          value = value.map(id => ({ _id: id }));
        }
        break;
      case 'equals':
        legacyOperator = '=';
        break;
      case 'notEquals':
        legacyOperator = '!=';
        break;
      case 'greaterThan':
        legacyOperator = '>';
        break;
      case 'lessThan':
        legacyOperator = '<';
        break;
      case 'greaterThanOrEqual':
        legacyOperator = '>=';
        break;
      case 'lessThanOrEqual':
        legacyOperator = '<=';
        break;
      case 'between':
        legacyOperator = 'BETWEEN';
        // Keep the between value object as-is
        break;
      case 'isEmpty':
        legacyOperator = 'isEmpty';
        break;
      case 'isNotEmpty':
        legacyOperator = 'isNotEmpty';
        break;
      case 'isNull':
        legacyOperator = 'isNull';
        break;
      case 'isNotNull':
        legacyOperator = 'isNotNull';
        break;
      default:
        // For unknown operators, try to use the operator name directly
        // This handles new operators that don't have legacy equivalents
        legacyOperator = condition.operator;
    }

    return {
      column: condition.field,
      value: value,
      condition: { value: legacyOperator, label: FILTER_OPERATORS[condition.operator]?.label || condition.operator }
    };
  });
};

/**
 * Create an empty filter group
 */
export const createEmptyFilterGroup = (operator = 'AND') => {
  return {
    version: 1,
    operator,
    conditions: [],
  };
};

/**
 * Create a new filter condition
 */
export const createFilterCondition = (field, operator = 'equals', value = null) => {
  return {
    field,
    operator,
    value,
  };
};

/**
 * Create a new nested filter group
 */
export const createNestedFilterGroup = (operator = 'OR') => {
  return {
    operator,
    conditions: [],
  };
};

/**
 * Add a condition to a filter group
 */
export const addConditionToGroup = (group, condition) => {
  return {
    ...group,
    conditions: [...group.conditions, condition],
  };
};

/**
 * Remove a condition from a filter group by index
 */
export const removeConditionFromGroup = (group, index) => {
  return {
    ...group,
    conditions: group.conditions.filter((_, i) => i !== index),
  };
};

/**
 * Update a condition in a filter group by index
 */
export const updateConditionInGroup = (group, index, updatedCondition) => {
  return {
    ...group,
    conditions: group.conditions.map((c, i) => i === index ? updatedCondition : c),
  };
};

/**
 * Get display label for a filter condition
 */
export const getConditionDisplayLabel = (condition, columnsMap) => {
  const columnName = condition.field;
  const operator = FILTER_OPERATORS[condition.operator]?.label || condition.operator;
  let value = condition.value;

  // Format value for display
  if (Array.isArray(value)) {
    value = value.length > 3 
      ? `${value.slice(0, 3).join(', ')}... (${value.length} items)`
      : value.join(', ');
  } else if (value && typeof value === 'object' && value.start !== undefined) {
    value = `${value.start} - ${value.end}`;
  } else if (value === null || value === undefined) {
    value = '';
  }

  // For isEmpty/isNotEmpty/isNull/isNotNull, no value needed
  if (['isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'].includes(condition.operator)) {
    return `${columnName} ${operator}`;
  }

  return `${columnName} ${operator} ${value}`;
};

// URL Encoding/Decoding utilities

/**
 * Simple LZ-string compatible compression using base64
 * For production, consider using the actual lz-string library
 */
export const compressToEncodedURIComponent = (input) => {
  try {
    const jsonString = JSON.stringify(input);
    // Use base64 encoding for now (can switch to lz-string later)
    const base64 = btoa(unescape(encodeURIComponent(jsonString)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (e) {
    console.error('Error compressing filter state:', e);
    return null;
  }
};

/**
 * Decompress from URL-safe base64
 */
export const decompressFromEncodedURIComponent = (compressed) => {
  try {
    if (!compressed) return null;
    // Restore base64 padding and characters
    let base64 = compressed.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
      base64 += '=';
    }
    const jsonString = decodeURIComponent(escape(atob(base64)));
    return JSON.parse(jsonString);
  } catch (e) {
    console.error('Error decompressing filter state:', e);
    return null;
  }
};

/**
 * Encode filter state to URL parameter
 */
export const encodeFilterState = (filterGroup) => {
  if (!filterGroup || !filterGroup.conditions || filterGroup.conditions.length === 0) {
    return null;
  }
  return compressToEncodedURIComponent(filterGroup);
};

/**
 * Decode filter state from URL parameter
 */
export const decodeFilterState = (encodedState) => {
  if (!encodedState) return null;
  const decoded = decompressFromEncodedURIComponent(encodedState);
  return normalizeFilters(decoded);
};

/**
 * Get current URL with filter state
 */
export const getUrlWithFilters = (tableName, filterGroup, columnOrder = null) => {
  const url = new URL(window.location.href);
  
  if (tableName) {
    url.searchParams.set('table', tableName);
  }
  
  const encodedFilters = encodeFilterState(filterGroup);
  if (encodedFilters) {
    url.searchParams.set('filters', encodedFilters);
  } else {
    url.searchParams.delete('filters');
  }
  
  if (columnOrder) {
    const encodedColumns = compressToEncodedURIComponent({ order: columnOrder });
    if (encodedColumns) {
      url.searchParams.set('columns', encodedColumns);
    }
  }
  
  return url.toString();
};

/**
 * Parse filters from current URL
 */
export const parseFiltersFromUrl = () => {
  const url = new URL(window.location.href);
  const tableName = url.searchParams.get('table');
  const filtersParam = url.searchParams.get('filters');
  const columnsParam = url.searchParams.get('columns');
  
  const filters = decodeFilterState(filtersParam);
  const columns = columnsParam ? decompressFromEncodedURIComponent(columnsParam) : null;
  
  return {
    tableName,
    filters,
    columnOrder: columns?.order || null,
  };
};

/**
 * Update URL without page reload
 */
export const updateUrlWithFilters = (tableName, filterGroup, columnOrder = null) => {
  const newUrl = getUrlWithFilters(tableName, filterGroup, columnOrder);
  window.history.replaceState({}, '', newUrl);
};

// Column order persistence utilities

const COLUMN_ORDER_KEY_PREFIX = 'dbInterface_columns_';

/**
 * Save column order to localStorage
 */
export const saveColumnOrder = (tableName, columnOrder) => {
  try {
    localStorage.setItem(`${COLUMN_ORDER_KEY_PREFIX}${tableName}`, JSON.stringify({
      order: columnOrder,
      savedAt: new Date().toISOString(),
    }));
  } catch (e) {
    console.error('Error saving column order:', e);
  }
};

/**
 * Load column order from localStorage
 */
export const loadColumnOrder = (tableName) => {
  try {
    const saved = localStorage.getItem(`${COLUMN_ORDER_KEY_PREFIX}${tableName}`);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.order || null;
    }
  } catch (e) {
    console.error('Error loading column order:', e);
  }
  return null;
};

/**
 * Clear saved column order for a table
 */
export const clearColumnOrder = (tableName) => {
  try {
    localStorage.removeItem(`${COLUMN_ORDER_KEY_PREFIX}${tableName}`);
  } catch (e) {
    console.error('Error clearing column order:', e);
  }
};

/**
 * Check if a value is considered "empty" for filtering purposes
 */
export const isEmptyValue = (value) => {
  return value === null || value === undefined || value === '';
};

/**
 * Check if operator requires a value input
 */
export const operatorNeedsValue = (operator) => {
  return !['isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'].includes(operator);
};

/**
 * Check if operator is a range operator (needs two values)
 */
export const isRangeOperator = (operator) => {
  return operator === 'between';
};
